import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { t } from "i18next";
import { toast } from "react-toastify";
import { CART_TYPE, DEFAULT_ORGANIZATION_CUSTOMER_NUMBER } from "shared";
import discountService from "webshop/services/discountService";
import { getMessageFromError } from "../../../shared/welda/services/utilsService";
import errorHandling from "../errorHandling";
import eCommerceService from "./eCommerceService";
import {
  buildPriceRequestKey,
  claimPriceRequest,
  getCompanyIdsForDossier,
  releasePriceRequest,
} from "./priceAndStockRequestUtils";
const initialState = {
  cart: null,
  cartModal: {
    show: false,
    product: null,
    type: null,
    quantity: 0,
  },
  // null = prices have not been fetched yet (usePriceAndStock treats this as loading).
  // [] = a fetch completed with no matching articles.
  /** @type {null | any[]} */
  customerPrices: null,
  companyPrices: {},
  isLoadingPricesAndStock: false,
  isLoadingCompanyPrices: false,
  // Customer number the prices in customerPrices belong to. Responses for
  // another customer (e.g. still in flight when the selection changed) are dropped.
  pricesCustomerId: DEFAULT_ORGANIZATION_CUSTOMER_NUMBER,
  // Products whose prices were requested, per request dossier, so prices can be
  // fetched again when the selected shop customer changes.
  pricedProducts: {},
  productLayout: "list",
  companyID: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",

  isFavoriteCartModalOpen: false,
  isMyAddressFormModalOpen: false,
  favoriteCarts: [],
  cartItemToAdd: {
    artNr: "",
    quantity: 0,
  },
  selectedProducts: [],
  isBulkProductMode: false,
};

export const GetDeliveryAddresses = createAsyncThunk(
  "cac/GetDeliveryAddresses",
  async (companyID, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const selectedCustomer = state.dias?.shopCustomer?.customer?._id;
      const companyID = selectedCustomer;
      const awaitGetDeliveryAddresses =
        await eCommerceService.GetDeliveryAddresses(companyID);
      return awaitGetDeliveryAddresses;
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

/** Customer number used for shop prices: the explicitly selected customer, or the default. */
export const getShopCustomerNr = (state) =>
  state.dias?.shopCustomer?.customer?.KLANTNR ||
  DEFAULT_ORGANIZATION_CUSTOMER_NUMBER;

export const GetArticlesPriceAndStock = createAsyncThunk(
  "cac/GetArticlesPriceAndStock",
  async (params, thunkAPI) => {
    try {
      const { products, dossier } = params;
      const state = thunkAPI.getState();
      const selectedCustomerCustomerId = getShopCustomerNr(state);

      const companyIds = getCompanyIdsForDossier(state.users.user, dossier);

      const data = await eCommerceService.GetArticlesPriceAndStock({
        products,
        dossier,
        selectedCustomerCustomerId,
        companyIds,
      });
      return { ...data, pricedForCustomerId: selectedCustomerCustomerId };
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  },
  {
    condition: (params, { getState }) => {
      const { products, dossier } = params;
      if (!products?.length) {
        return false;
      }

      const state = getState();
      const selectedCustomerCustomerId = getShopCustomerNr(state);
      const companyIds = getCompanyIdsForDossier(state.users.user, dossier);
      const requestKey = buildPriceRequestKey({
        products,
        dossier,
        selectedCustomerCustomerId,
        companyIds,
      });

      return claimPriceRequest(requestKey);
    },
  }
);

/**
 * Selected shop customer changed: drop all prices of the previous customer and
 * fetch every product shown so far again for the new customer.
 */
export const refreshPricesForShopCustomer = createAsyncThunk(
  "ecommerce/refreshPricesForShopCustomer",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const customerId = getShopCustomerNr(state);
    thunkAPI.dispatch(eCommerceSlice.actions.resetPricesForCustomer(customerId));
    const pricedProducts = state.ecommerce?.pricedProducts ?? {};
    Object.entries(pricedProducts).forEach(([dossier, productsByKey]) => {
      const products = Object.values(productsByKey ?? {});
      if (products.length > 0) {
        thunkAPI.dispatch(GetArticlesPriceAndStock({ products, dossier }));
      }
    });
  }
);

export const addToCart = createAsyncThunk(
  "ecommerce/addToCart",
  async (input, thunkAPI) => {
    try {
      const { products, modal = { show: false }, cartId = null } = input;
      const data = await eCommerceService.addToCart({ products, cartId });
      const { discountMessageKey, discountMessageParams } = data;
      if (discountMessageKey) {
        toast.warn(t(`${discountMessageKey}.text`, discountMessageParams));
      }
      const cart = data.cart;
      const payload = {
        cart,
        modal: {
          ...modal,
          product: products[0],
          type: "add",
        },
      };
      return payload;
    } catch (error) {
      const message = getMessageFromError(error);
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  "ecommerce/removeFromCart",
  async (input, thunkAPI) => {
    try {
      const { products, modal = { show: false }, cartId } = input;
      const data = await eCommerceService.removeFromCart({
        products,
        cartId,
      });
      const { discountMessageKey, discountMessageParams } = data;
      if (discountMessageKey) {
        toast.warn(t(`${discountMessageKey}.text`, discountMessageParams));
      }
      const cart = data.cart;
      const payload = {
        cart,
      };
      return payload;
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const clearCart = createAsyncThunk(
  "ecommerce/clearCart",
  async (input, thunkAPI) => {
    try {
      const data = await eCommerceService.clearCart(input);
      const { discountMessageKey, discountMessageParams } = data;
      if (discountMessageKey) {
        toast.warn(t(`${discountMessageKey}.text`, discountMessageParams));
      }
      const payload = { cart: data.cart };
      return payload;
    } catch (error) {
      const message = getMessageFromError(error);
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const setCartPickupOrDeliveryTermCode = createAsyncThunk(
  "ecommerce/setCartPickupOrDeliveryTermCode",
  async (input, thunkAPI) => {
    try {
      const { pickupOrDeliveryTermCode } = input;
      const data = await eCommerceService.setCartPickupOrDeliveryTermCode({
        pickupOrDeliveryTermCode,
      });
      const { discountMessageKey, discountMessageParams } = data;
      if (discountMessageKey) {
        toast.warn(t(`${discountMessageKey}.text`, discountMessageParams));
      }
      const payload = {
        cart: data.cart,
      };
      return payload;
    } catch (error) {
      const message = getMessageFromError(error);
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getCart = createAsyncThunk(
  "ecommerce/getCart",
  async (input, thunkAPI) => {
    try {
      const data = await eCommerceService.getCart();

      const payload = {
        cart: data.cart,
      };
      return payload;
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getFavoriteCarts = createAsyncThunk(
  "ecommerce/getFavoriteCarts",
  async (input, thunkAPI) => {
    const data = await eCommerceService.getFavoriteCarts();

    const payload = {
      favoriteCarts: data.favoriteCarts,
    };
    return payload;
  }
);

export const createFavoriteCart = createAsyncThunk(
  "ecommerce/createFavoriteCart",
  async (input, thunkAPI) => {
    const data = await eCommerceService.createFavoriteCart(input);
    const payload = {
      newFavoriteCart: data.newFavoriteCart,
    };
    return payload;
  }
);

export const deleteFavoriteCart = createAsyncThunk(
  "ecommerce/deleteFavoriteCart",
  async (input, thunkAPI) => {
    try {
      const data = await eCommerceService.deleteFavoriteCart(input);
      const payload = { deletedFavoriteCartId: data.deletedFavoriteCartId };
      return payload;
    } catch (error) {
      console.error("deleteFavoriteCart error:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete favorite cart";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const editFavoriteCart = createAsyncThunk(
  "ecommerce/editFavoriteCart",
  async (input, thunkAPI) => {
    const data = await eCommerceService.editFavoriteCart(input);
    const payload = {
      updatedFavoriteCart: data.updatedFavoriteCart,
    };
    return payload;
  }
);

export const applyDiscountCodeToCart = createAsyncThunk(
  "ecommerce/applyDiscountCodeToCart",
  async (input, thunkAPI) => {
    const data = await discountService.applyDiscountCode(input);
    const { discountMessageKey, discountMessageParams } = data;
    if (discountMessageKey) {
      toast.warn(t(`${discountMessageKey}.text`, discountMessageParams));
    }
    const payload = { ...data };
    return payload;
  }
);

export const removeDiscountCodeFromCart = createAsyncThunk(
  "ecommerce/removeDiscountCodeFromCart",
  async (input, thunkAPI) => {
    const data = await discountService.removeDiscountCodeFromCart();
    const { discountMessageKey, discountMessageParams } = data;
    if (discountMessageKey) {
      toast.warn(t(`${discountMessageKey}.text`, discountMessageParams));
    }
    const payload = { ...data };
    return payload;
  }
);

export const eCommerceSlice = createSlice({
  name: "ecommerce",
  initialState,
  reducers: {
    reset: (state) => {
      Object.assign(state, initialState);
    },
    resetCustomerPrices: (state) => {
      state.customerPrices = null;
    },
    resetPricesForCustomer: (state, action) => {
      state.customerPrices = null;
      state.companyPrices = {};
      state.pricesCustomerId =
        action.payload || DEFAULT_ORGANIZATION_CUSTOMER_NUMBER;
      state.isLoadingPricesAndStock = false;
      state.isLoadingCompanyPrices = false;
      releasePriceRequest();
    },
    resetPricesAndStock: (state) => {
      state.customerPrices = null;
      state.companyPrices = {};
      state.isLoadingPricesAndStock = false;
      state.isLoadingCompanyPrices = false;
      releasePriceRequest();
    },
    toggleCartModal: (state) => {
      state.cartModal.show = !state.cartModal.show;
    },
    setProductLayout: (state, action) => {
      state.productLayout = action.payload;
    },
    openAddToFavoriteCartModal: (state, action) => {
      state.isFavoriteCartModalOpen = true;
      state.cartItemToAdd = action.payload;
    },
    openMyAddressFormModal: (state, action) => {
      state.isMyAddressFormModalOpen = true;
    },
    closeMyAddressFormModal: (state) => {
      state.isMyAddressFormModalOpen = false;
    },
    closeAddToFavoriteCartModal: (state) => {
      state.isFavoriteCartModalOpen = false;
    },
    setFavoriteCarts: (state, action) => {
      state.favoriteCarts = action.payload;
    },
    resetFavoriteCarts: (state) => {
      state.favoriteCarts = [];

      state.isFavoriteCartModalOpen = false;
    },
    addSelectedProducts: (state, action) => {
      const selectedProducts = action.payload;
      state.selectedProducts = [...state.selectedProducts, ...selectedProducts];
      state.isBulkProductMode = state.selectedProducts.length > 0;
    },
    changeSelectedProductsQuantity: (state, action) => {
      if (state.isBulkProductMode) {
        const { identifier, quantity } = action.payload;
        state.selectedProducts = state.selectedProducts.map((product) =>
          product.identifier === identifier ? { ...product, quantity } : product
        );
      }
    },
    removeSelectedProducts: (state, action) => {
      const unselectedProductsIdentifiers = action.payload;
      state.selectedProducts = state.selectedProducts.filter(
        (product) => !unselectedProductsIdentifiers.includes(product.identifier)
      );
      state.isBulkProductMode = state.selectedProducts.length > 0;
    },
    resetSelectedProducts: (state) => {
      state.selectedProducts = [];
      state.isBulkProductMode = false;
    },
    unmountCart: (state) => {
      state.cart = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(applyDiscountCodeToCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(applyDiscountCodeToCart.fulfilled, (state, action) => {
        const {
          isValid,
          discountMessageKey,
          discountMessageParams,
          cart,
        } = action.payload;
        if (isValid) {
          state.cart = cart;
        } else {
          state.message = discountMessageKey
            ? t(`${discountMessageKey}.text`, discountMessageParams)
            : "";
        }
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(applyDiscountCodeToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
      })

      .addCase(removeDiscountCodeFromCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(removeDiscountCodeFromCart.fulfilled, (state, action) => {
        const { cart } = action.payload;
        state.cart = cart;
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(removeDiscountCodeFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
      })

      .addCase(getCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(getCart.fulfilled, (state, action) => {
        const { cart } = action.payload;
        state.cart = cart;
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(getCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
      })

      .addCase(addToCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        const { cart = null, modal } = action.payload;
        if (cart.cartType === CART_TYPE.CHECKOUT) {
          state.cart = cart;
          state.cartModal = {
            show: modal.show,
            product: cart.cartItems.find(
              (art) => art.artNr == modal.product.artNr
            ),
            quantity: modal.product.quantity,
            type: "add",
          };
          state.selectedProducts = [];
          state.isBulkProductMode = false;
        } else if (cart.cartType === CART_TYPE.FAVORITES) {
          const index = state.favoriteCarts.findIndex(
            (c) => c._id === cart._id
          );
          if (index !== -1) {
            state.favoriteCarts[index] = cart;
          }
        }

        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(removeFromCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        const { modal, cart = null } = action.payload;
        if (cart.cartType === CART_TYPE.CHECKOUT) {
          state.cart = cart;
        } else if (cart.cartType === CART_TYPE.FAVORITES) {
          const index = state.favoriteCarts.findIndex(
            (c) => c._id === cart._id
          );
          if (index !== -1) {
            state.favoriteCarts[index] = cart;
          }
        }
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(clearCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        const { cart = null } = action.payload;
        if (cart.cartType === CART_TYPE.CHECKOUT) {
          state.cart = cart;
        } else if (cart.cartType === CART_TYPE.FAVORITES) {
          const index = state.favoriteCarts.findIndex(
            (c) => c._id === cart._id
          );
          if (index !== -1) {
            state.favoriteCarts[index] = cart;
          }
        }
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(GetDeliveryAddresses.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(GetDeliveryAddresses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.deliveryAddresses = action.payload.Addresses;
      })
      .addCase(GetDeliveryAddresses.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.deliveryAddresses = null;
      })

      .addCase(GetArticlesPriceAndStock.fulfilled, (state, action) => {
        const pricedForCustomerId = action.payload?.pricedForCustomerId;
        if (
          pricedForCustomerId != null &&
          String(pricedForCustomerId) !== String(state.pricesCustomerId)
        ) {
          // Prices for a customer that is no longer selected: never show them.
          releasePriceRequest();
          state.isLoadingPricesAndStock = false;
          state.isLoadingCompanyPrices = false;
          return;
        }
        const newArticles = action.payload.Articles || [];
        const existingArticles = state.customerPrices || [];
        const mergedArticles = [...existingArticles];
        newArticles.forEach((newArticle) => {
          const existingIndex = mergedArticles.findIndex(
            (existing) => existing.ProductId === newArticle.ProductId
          );

          if (existingIndex !== -1) {
            // Replace existing article with new one
            mergedArticles[existingIndex] = newArticle;
          } else {
            // Add new article
            mergedArticles.push(newArticle);
          }
        });

        const newCompanyArticles = action.payload.CompanyArticles || {};
        const existingCompanyArticles = state.companyPrices || {};
        const mergedCompanyArticles = { ...existingCompanyArticles };

        // Loop over object entries: each key is companyCustomerNr, value is array of articles
        Object.entries(newCompanyArticles).forEach(
          ([companyCustomerNr, newArticles]) => {
            // Get existing articles for this company, or initialize empty array
            const existingCompanyArticlesArray =
              mergedCompanyArticles[companyCustomerNr]?.articles || [];
            const mergedCompanyArticlesArray = [
              ...existingCompanyArticlesArray,
            ];

            // Merge new articles with existing ones for this company
            newArticles.articles.forEach((newCompanyArticle) => {
              const existingIndex = mergedCompanyArticlesArray.findIndex(
                (existing) => existing.ProductId === newCompanyArticle.ProductId
              );
              if (existingIndex !== -1) {
                // Replace existing article with new one
                mergedCompanyArticlesArray[existingIndex] = newCompanyArticle;
              } else {
                // Add new article
                mergedCompanyArticlesArray.push(newCompanyArticle);
              }
            });

            // Update the merged articles for this company
            mergedCompanyArticles[companyCustomerNr] = {
              articles: mergedCompanyArticlesArray,
              companyName: newArticles.companyName,
            };
          }
        );

        state.customerPrices = mergedArticles;
        state.companyPrices = mergedCompanyArticles;
        releasePriceRequest();
        state.isLoadingPricesAndStock = false;
        state.isLoadingCompanyPrices = false;
        state.isSuccess = true;
      })
      .addCase(GetArticlesPriceAndStock.pending, (state, action) => {
        const { products, dossier } = action.meta.arg ?? {};
        if (dossier != null && Array.isArray(products)) {
          const byKey = state.pricedProducts[dossier] ?? {};
          products.forEach((product) => {
            if (product?.Nummer == null) return;
            byKey[`${product.Dossier}:${product.Nummer}`] = {
              Nummer: product.Nummer,
              Dossier: product.Dossier,
            };
          });
          state.pricedProducts[dossier] = byKey;
        }
        state.isLoadingPricesAndStock = true;
        state.isLoadingCompanyPrices = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(GetArticlesPriceAndStock.rejected, (state, action) => {
        releasePriceRequest();
        // Keep as empty array (not null) so UI stops loading and treats
        // missing prices as unavailable rather than spinning forever.
        state.customerPrices = [];
        state.companyPrices = {};
        state.isLoadingPricesAndStock = false;
        state.isLoadingCompanyPrices = false;
        state.isError = true;
      })

      .addCase(setCartPickupOrDeliveryTermCode.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(setCartPickupOrDeliveryTermCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.cart = action.payload.cart;
      })
      .addCase(setCartPickupOrDeliveryTermCode.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
      })

      .addCase(getFavoriteCarts.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(getFavoriteCarts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.favoriteCarts = action.payload.favoriteCarts ?? [];
      })
      .addCase(getFavoriteCarts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message;
      })

      .addCase(createFavoriteCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(createFavoriteCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.favoriteCarts = [
          ...state.favoriteCarts,
          action.payload.newFavoriteCart,
        ];
      })
      .addCase(createFavoriteCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
      })

      .addCase(deleteFavoriteCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(deleteFavoriteCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const deletedCartId = action.payload?.deletedFavoriteCartId;
        if (deletedCartId) {
          state.favoriteCarts = state.favoriteCarts.filter(
            (c) => String(c._id) !== String(deletedCartId)
          );
        }
      })
      .addCase(deleteFavoriteCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to delete favorite cart";
      })

      .addCase(editFavoriteCart.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(editFavoriteCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const { updatedFavoriteCart } = action.payload;
        state.favoriteCarts = state.favoriteCarts.map((c) =>
          c._id === updatedFavoriteCart._id ? updatedFavoriteCart : c
        );
      })
      .addCase(editFavoriteCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
      });
  },
});

export const {
  reset,
  resetCustomerPrices,
  resetPricesAndStock,
  resetPricesForCustomer,
  toggleCartModal,
  setProductLayout,
  openAddToFavoriteCartModal,
  closeAddToFavoriteCartModal,
  setFavoriteCarts,
  resetFavoriteCarts,
  addSelectedProducts,
  changeSelectedProductsQuantity,
  removeSelectedProducts,
  resetSelectedProducts,
  openMyAddressFormModal,
  closeMyAddressFormModal,
  unmountCart,

  // setShowCustomerPrice,
  // updateCartProduct,
  // updateCart,
} = eCommerceSlice.actions;

// Selectors
export const selectIsBulkMode = (state) => state.ecommerce.isBulkProductMode;
export const selectSelectedProducts = (state) =>
  state.ecommerce.selectedProducts;

export default eCommerceSlice.reducer;
